// ProtectedRoute.jsx — Complete fixed version
// - resolveUserId returns ONLY numeric userId (ObjectId is never used)
// - Falls back to localStorage "userId" / "userData" when auth user only has ObjectId (after reload)
// - Adds Authorization header to permission fetch
// - No more infinite loading when numeric userId is missing
import { useEffect, useState } from "react";
import { useAuth } from "./login/AuthProvider";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { FaLock, FaArrowLeft, FaSignOutAlt } from "react-icons/fa";

// ============ ANIMATED ACCESS DENIED COMPONENT ============
const AnimatedAccessDenied = ({
  title = "Access Denied",
  message = "You do not have permission to access this page.",
  subMessage = null,
  onLogout,
}) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        flexDirection: "column",
        backgroundColor: "#f8f9fa",
        color: "#495057",
        padding: "20px",
        textAlign: "center",
      }}
    >
      <style>{`
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-15px) rotate(90deg); }
          50% { transform: translateY(0) rotate(180deg); }
          75% { transform: translateY(-15px) rotate(270deg); }
          100% { transform: translateY(0) rotate(360deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { filter: drop-shadow(0 0 15px rgba(0, 74, 173, 0.5)); }
          50% { filter: drop-shadow(0 0 35px rgba(0, 74, 173, 0.95)); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .access-icon-wrapper {
          font-size: 90px;
          line-height: 1;
          margin-bottom: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #004aad;
          user-select: none;
          animation: floatIcon 4s ease-in-out infinite, pulseGlow 2.5s ease-in-out infinite;
        }
        .access-title {
          font-size: 28px;
          margin-bottom: 12px;
          color: #004aad;
          font-weight: 700;
          animation: fadeInUp 0.6s ease-out;
        }
        .access-message {
          font-size: 16px;
          color: #495057;
          margin-bottom: 8px;
          max-width: 480px;
          animation: fadeInUp 0.6s ease-out 0.15s backwards;
        }
        .access-submessage {
          font-size: 14px;
          color: #6c757d;
          margin-top: 6px;
          animation: fadeInUp 0.6s ease-out 0.3s backwards;
        }
        .access-btn-container {
          display: flex;
          gap: 12px;
          margin-top: 28px;
          flex-wrap: wrap;
          justify-content: center;
          animation: fadeInUp 0.6s ease-out 0.45s backwards;
        }
        .access-btn {
          padding: 12px 26px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 15px;
          font-weight: 600;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .access-btn-secondary {
          background: white;
          color: #004aad;
          border: 2px solid #004aad;
        }
        .access-btn-secondary:hover {
          transform: translateY(-3px);
          background: #004aad;
          color: white;
          box-shadow: 0 8px 25px rgba(0, 74, 173, 0.4);
        }
        .access-btn-danger {
          background: linear-gradient(135deg, #dc3545, #e4606d);
          color: white;
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
        }
        .access-btn-danger:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(220, 53, 69, 0.5);
          background: linear-gradient(135deg, #c82333, #d9534f);
        }
        .access-btn-icon {
          font-size: 16px;
        }
      `}</style>

      <div className="access-icon-wrapper">
        <FaLock />
      </div>

      <h1 className="access-title">{title}</h1>
      <p className="access-message">{message}</p>
      {subMessage && <p className="access-submessage">{subMessage}</p>}

      <div className="access-btn-container">
        <button
          className="access-btn access-btn-secondary"
          onClick={() => window.history.back()}
        >
          <FaArrowLeft className="access-btn-icon" /> Go Back
        </button>
        <button className="access-btn access-btn-danger" onClick={onLogout}>
          <FaSignOutAlt className="access-btn-icon" /> Logout
        </button>
      </div>
    </div>
  );
};

// ============ HELPERS ============
const readFromLocalStorage = (extractor) => {
  try {
    const possibleKeys = [
      "userData", // contains numeric userId (e.g. 15)
      "userProfile",
      "user",
      "auth",
      "currentUser",
      "labUser",
      "labUserData",
    ];

    for (const key of possibleKeys) {
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      try {
        const parsed = JSON.parse(stored);
        const value = extractor(parsed);
        if (value !== undefined && value !== null && value !== "") return value;
      } catch (_) {}
    }
    return null;
  } catch (_) {
    return null;
  }
};

// ✅ Returns a Number ONLY if the value is a pure integer (e.g. 15 or "15").
//    ObjectIds like "6abc86c9f3ebcf33081ec7f1" are rejected (return null).
const toNumericId = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return null; // reject ObjectId / non-integer
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
};

const firstNumericId = (candidates) => {
  for (const v of candidates) {
    const n = toNumericId(v);
    if (n !== null) return n;
  }
  return null;
};

// ✅ Only numeric userId is ever returned. _id / id (ObjectId) are NOT used.
const resolveUserId = (authUser) => {
  // 1. From auth user (numeric userId only)
  if (authUser) {
    const n = firstNumericId([
      authUser.userId,
      authUser.userid,
      authUser?.data?.userId,
      authUser?.user?.userId,
    ]);
    if (n !== null) return n;
  }

  // 2. Direct localStorage numeric key
  try {
    const n = toNumericId(localStorage.getItem("userId"));
    if (n !== null) return n;
  } catch (_) {}

  // 3. JSON stores in localStorage (userData, userProfile, ...)
  return readFromLocalStorage((parsed) =>
    firstNumericId([
      parsed?.userId,
      parsed?.userid,
      parsed?.data?.userId,
      parsed?.user?.userId,
    ])
  );
};

const resolveUserRole = (authUser) => {
  if (authUser) {
    const fromAuth =
      authUser.userRole ??
      authUser.role ??
      authUser?.data?.userRole ??
      authUser?.data?.role ??
      authUser?.user?.userRole ??
      authUser?.user?.role;
    if (fromAuth) return String(fromAuth).trim();
  }
  const fromDirect = localStorage.getItem("userRole");
  if (fromDirect) return String(fromDirect).trim();

  return readFromLocalStorage((parsed) => {
    const v =
      parsed?.userRole ??
      parsed?.role ??
      parsed?.data?.userRole ??
      parsed?.data?.role ??
      parsed?.user?.userRole ??
      parsed?.user?.role;
    return v ? String(v).trim() : null;
  });
};

const isAdminRole = (role) => {
  if (!role) return false;
  const r = String(role).trim().toLowerCase();
  return (
    r === "admin" ||
    r === "superadmin" ||
    r === "super-admin" ||
    r === "super_admin"
  );
};

// ============ MAIN PROTECTED ROUTE ============
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    localStorage.setItem("redirectAfterLogin", currentPath);
    console.log("Saving redirect path:", currentPath);
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

// ============ PERMISSION-BASED PROTECTED ROUTE ============
export const ProtectedRouteWithPermission = ({ element: Component, path }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [permittedPaths, setPermittedPaths] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const userId = resolveUserId(user); // numeric only
  const userRole = resolveUserRole(user);
  const isAdmin = isAdminRole(userRole);

  useEffect(() => {
    // Admin skips permission fetch
    if (isAdmin) {
      setIsLoadingPermissions(false);
      return;
    }

    // Still hydrating auth — wait
    if (loading) return;

    // Not logged in — render handles redirect
    if (!user) {
      setIsLoadingPermissions(false);
      return;
    }

    // Logged in but no numeric userId could be resolved — stop spinner, show error
    if (userId === null || userId === undefined) {
      setPermittedPaths([]);
      setFetchError(true);
      setIsLoadingPermissions(false);
      return;
    }

    let cancelled = false;
    setIsLoadingPermissions(true);
    setFetchError(false);

    const token = localStorage.getItem("accessToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const url = `https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pages`;
    console.log("[ProtectedRouteWithPermission] Fetching:", url, "userId:", userId);

    fetch(url, { headers })
      .then((res) => {
        console.log("[ProtectedRouteWithPermission] Status:", res.status);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;

        if (Array.isArray(data)) {
          const paths = data
            .filter(
              (permission) =>
                permission && permission.page && permission.isVisible
            )
            .map((permission) => permission.page.path);
          setPermittedPaths(paths);
        } else {
          console.error("[ProtectedRouteWithPermission] Invalid data:", data);
          setPermittedPaths([]);
        }
        setIsLoadingPermissions(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[ProtectedRouteWithPermission] Fetch error:", err);
        setPermittedPaths([]);
        setFetchError(true);
        setIsLoadingPermissions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isAdmin, loading, user]);

  const handleLogout = () => {
    localStorage.removeItem("redirectAfterLogin");
    if (typeof logout === "function") {
      logout();
    } else {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  if (loading || isLoadingPermissions) {
    return <div>Loading...</div>;
  }

  if (!user) {
    const currentPath = location.pathname + location.search;
    localStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (isAdmin) {
    return <Component />;
  }

  if (fetchError) {
    console.warn("[ProtectedRouteWithPermission] Fetch failed", {
      userId,
      url: `https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pages`,
      hasToken: !!localStorage.getItem("accessToken"),
    });
    return (
      <AnimatedAccessDenied
        title="Could Not Verify Access"
        message="We could not load your permissions. Please try again or logout."
        subMessage={`Route: ${path} • User ID: ${userId ?? "N/A"}`}
        onLogout={handleLogout}
      />
    );
  }

  const cleanPath = (routePath) => {
    if (!routePath) return "";
    return routePath.split("?")[0].split("#")[0];
  };

  const currentPath = cleanPath(path);
  const isPermitted = permittedPaths.some((permittedPath) => {
    const cleanPermittedPath = cleanPath(permittedPath);

    if (cleanPermittedPath === currentPath) return true;

    if (cleanPermittedPath.includes(":") && currentPath.includes("/:")) {
      const permittedParts = cleanPermittedPath.split("/");
      const currentParts = currentPath.split("/");

      if (permittedParts.length !== currentParts.length) return false;

      for (let i = 0; i < permittedParts.length; i++) {
        const permittedPart = permittedParts[i];
        const currentPart = currentParts[i];
        if (permittedPart.startsWith(":")) continue;
        if (permittedPart !== currentPart) return false;
      }
      return true;
    }

    return false;
  });

  if (!isPermitted) {
    console.warn("[ProtectedRouteWithPermission] Access denied", {
      routePath: currentPath,
      permittedPaths,
      userRole,
      isAdmin,
      userId,
    });

    return (
      <AnimatedAccessDenied
        title="Access Denied"
        message="You do not have permission to access this page."
        onLogout={handleLogout}
      />
    );
  }

  return <Component />;
};

// ============ ROLE-BASED PROTECTED ROUTE ============
export const RoleProtectedRoute = ({
  element: Component,
  allowedRoles = [],
}) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  const userRole = resolveUserRole(user);

  const handleLogout = () => {
    localStorage.removeItem("redirectAfterLogin");
    if (typeof logout === "function") {
      logout();
    } else {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    const currentPath = location.pathname + location.search;
    localStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  const hasRequiredRole = allowedRoles.includes(userRole);

  if (!hasRequiredRole) {
    return (
      <AnimatedAccessDenied
        title="Access Restricted"
        message={`This page is only accessible to: ${allowedRoles.join(", ")}`}
        subMessage={`Your role: ${userRole || "Not assigned"}`}
        onLogout={handleLogout}
      />
    );
  }
  return <Component />;
};

export default ProtectedRoute;